<ng-container *ngIf="!isLoading; else loading">
  <form class="form" [formGroup]="form">

    <div class="form-section">
      <div class="form-section-title">
        <h6 appLocalizationPopupDirective localizationCode="Adp-Credentials">{{ 'Adp-Credentials' | translate: {Default: 'Credentials'} }}</h6>
      </div>
      <div class="form-section-contents">
        <div class="section-row">
          <mat-form-field appearance="fill">
            <mat-label>{{ 'Adp-ClientId' | translate: {Default: 'Client Id'} }}</mat-label>
            <input matInput formControlName="clientId">
          </mat-form-field>

          <mat-form-field appearance="fill">
            <mat-label>{{ 'Adp-ClientSecret' | translate: {Default: 'Client Secret'} }}</mat-label>
            <input matInput type="password" formControlName="clientSecret">
            <mat-hint *ngIf="integrationConfig?.hasClientSecret">{{ 'Adp-SecretConfigured' | translate: {Default: 'Already configured - leave blank to keep it, or enter a new one to change it.'} }}</mat-hint>
          </mat-form-field>
        </div>
      </div>
    </div>

    <div class="form-section" *ngIf="!hasCertificate">
      <div class="form-section-title">
        <h6 appLocalizationPopupDirective localizationCode="Adp-ClientCertificate">{{ 'Adp-ClientCertificate' | translate: {Default: 'Client Certificate'} }}</h6>
      </div>
      <div class="form-section-contents">
        <p class="section-hint">{{ 'Adp-ClientCertificateFirstSetupHint' | translate: {Default: 'Upload the certificate once. After saving, use Update Certificate to replace it.'} }}</p>

        <div class="section-row">
          <div class="certificate-upload">
            <label>{{ 'Adp-Certificate' | translate: {Default: 'Certificate File (.pfx)'} }}</label>
            <input type="file" accept=".pfx,.p12" (change)="onCertificateSelected($event)">
          </div>

          <mat-form-field appearance="fill">
            <mat-label>{{ 'Adp-CertificatePassword' | translate: {Default: 'Certificate Password'} }}</mat-label>
            <input matInput type="password" formControlName="certificatePassword">
          </mat-form-field>
        </div>
      </div>
    </div>

    <div class="form-section">
      <div class="form-section-title">
        <h6 appLocalizationPopupDirective localizationCode="Adp-Connection">{{ 'Adp-Connection' | translate: {Default: 'Connection'} }}</h6>
      </div>
      <div class="form-section-contents">
        <div class="section-row">
          <mat-form-field appearance="fill">
            <mat-label>{{ 'Adp-AuthUrl' | translate: {Default: 'Auth URL'} }}</mat-label>
            <input matInput formControlName="authUrl">
          </mat-form-field>

          <mat-form-field appearance="fill">
            <mat-label>{{ 'Adp-BaseUrl' | translate: {Default: 'Base URL'} }}</mat-label>
            <input matInput formControlName="baseUrl">
          </mat-form-field>
        </div>
      </div>
    </div>

    <div class="form-section">
      <div class="form-section-title">
        <h6 appLocalizationPopupDirective localizationCode="Adp-DefaultOrganization">{{ 'Adp-DefaultOrganization' | translate: {Default: 'Default Organization'} }}</h6>
      </div>
      <div class="form-section-contents">
        <p class="section-hint">{{ 'Adp-DefaultOrganizationHint' | translate: {Default: 'New employees created by the import are placed in this organization.'} }}</p>

        <div class="section-row">
          <kendo-dropdowntree
            class="dropdowntree"
            kendoDropDownTreeExpandable
            [kendoDropDownTreeFlatBinding]="organizationTree"
            valueField="id"
            textField="name"
            parentIdField="parent"
            formControlName="defaultOrganization"
            [filterable]="true"
            [expandOnFilter]="organizationFilterExpandSettings"
          >
            <ng-template kendoDropDownTreeNodeTemplate let-dataItem>
              <div class="mat-form-field-flex">
                {{ dataItem?.name }} ({{ dataItem?.type }})
              </div>
            </ng-template>

            <ng-template kendoDropDownTreeValueTemplate let-dataItem>
              <div class="treeview-value">
                {{ dataItem?.name }}
              </div>
            </ng-template>
          </kendo-dropdowntree>
        </div>
      </div>
    </div>

    <div class="form-section">
      <div class="form-section-title">
        <h6 appLocalizationPopupDirective localizationCode="Adp-Locale">{{ 'Adp-Locale' | translate: {Default: 'Locale' } }}</h6>
      </div>
      <div class="form-section-contents">
        <div class="section-row">
          <mat-form-field appearance="fill">
            <mat-label>{{ 'Culture' | translate: {Default: 'Culture'} }}</mat-label>
            <mat-select disableOptionCentering formControlName="cultureId">
              <mat-option *ngFor="let culture of cultures" [value]="culture.id">
                {{ culture.text }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>
    </div>
  </form>

  <div class="integration-form-footer">
    <button
      mat-flat-button
      setColor="primaryColour"
      color="primary"
      appPreventDoubleClick (throttledClick)="save()"
      [disabled]="!form.valid || (!hasCertificate && !selectedCertificate)"
    >{{ 'Save' | translate: {Default: 'Save'} }}</button>
  </div>

  <div class="form rotate-certificate" *ngIf="hasCertificate">
    <div class="form-section">
      <div class="form-section-title">
        <h6 appLocalizationPopupDirective localizationCode="Adp-UpdateCertificate">{{ 'Adp-UpdateCertificate' | translate: {Default: 'Update Certificate'} }}</h6>
      </div>
      <div class="form-section-contents">
        <p class="section-hint">{{ 'Adp-UpdateCertificateHint' | translate: {Default: 'Rotate the client certificate when it expires, without re-entering the other connection settings.'} }}</p>

        <ng-container *ngTemplateOutlet="certificateExpiry"></ng-container>

        <div class="section-row">
          <div class="certificate-upload">
            <label>{{ 'Adp-Certificate' | translate: {Default: 'Certificate File (.pfx)'} }}</label>
            <input type="file" accept=".pfx,.p12" (change)="onRotateCertificateSelected($event)">
          </div>

          <mat-form-field appearance="fill">
            <mat-label>{{ 'Adp-CertificatePassword' | translate: {Default: 'Certificate Password'} }}</mat-label>
            <input matInput type="password" [(ngModel)]="rotateCertificatePassword" [ngModelOptions]="{standalone: true}">
          </mat-form-field>
        </div>

        <button
          mat-stroked-button
          setColor="primaryColour"
          color="primary"
          appPreventDoubleClick (throttledClick)="updateCertificate()"
          [disabled]="!rotateCertificate"
        >{{ 'Adp-UpdateCertificate' | translate: {Default: 'Update Certificate'} }}</button>
      </div>
    </div>
  </div>
</ng-container>

<ng-template #loading>
  <ngx-skeleton-loader count="5" [theme]="{ 'height.px': 50 }"></ngx-skeleton-loader>
</ng-template>

<ng-template #certificateExpiry>
  <div class="certificate-expiry" *ngIf="integrationConfig?.hasCertificate" [ngClass]="'expiry-' + certificateExpiryStatus">
    <mat-icon class="material-icons-outlined">{{ certificateExpiryIcon }}</mat-icon>

    <ng-container *ngIf="certificateExpiresOn; else expiryUnreadable">
      <span *ngIf="!certificateExpired">
        {{ 'Adp-CertificateExpires' | translate: {Default: 'Certificate expires'} }}
        <strong>{{ certificateExpiresOn | date: 'mediumDate' }}</strong>
        ({{ 'Adp-CertificateDaysRemaining' | translate: {Default: 'Days remaining'} }}: {{ certificateDaysRemaining }})
      </span>
      <span *ngIf="certificateExpired">
        {{ 'Adp-CertificateExpired' | translate: {Default: 'Certificate expired'} }}
        <strong>{{ certificateExpiresOn | date: 'mediumDate' }}</strong>
      </span>
    </ng-container>

    <ng-template #expiryUnreadable>
      <span>{{ 'Adp-CertificateExpiryUnreadable' | translate: {Default: 'The certificate expiry date could not be read - check the certificate password.'} }}</span>
    </ng-template>
  </div>
</ng-template>
